import { Checkbox, FontWeights, IShimmerElement, Icon, Link, Shimmer, ShimmerElementType } from '@fluentui/react';
import { makeStyles, mergeClasses } from '@fluentui/react-components';
import React from 'react';
import { Field } from 'react-final-form';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { IntrinsicTask } from '../../../constants/customization';
import { FeatureFlagName } from '../../../constants/features';
import { SupportSectionLinks } from '../../../constants/support-section-links';
import { Status } from '../../../models/common';
import {
    CustomizationTaskListValidationError,
    CustomizationTaskListValidationStatus,
} from '../../../models/customization';
import { ProjectFromDiscoveryService, ProjectResource } from '../../../models/project';
import { useHorizontalStackStyles, useStackWithFullWidthItemStyles } from '../../../themes/styles/flexbox-styles';
import { SerializableMap } from '../../../types/serializable-map';
import { isFeatureFlagEnabled } from '../../../utilities/features';
import { has, values } from '../../../utilities/serializable-map';
import { FluentIconNames } from '../../common/fluent-icon-names';
import Label from '../../common/form/label';
import Separator from '../../common/form/separator';
import { ArchitectureReadonlyField } from '../../dev-box/architecture-readonly-field';
import { HibernateReadonlyField } from '../../dev-box/hibernate-readonly-field';
import { ScheduleReadonlyField } from '../../dev-box/schedule-readonly-field';
import {
    AddDevBoxFormField,
    AddDevBoxFormProjectViewModel,
    CustomizationData,
    ImageViewModel,
    ImageViewModelMap,
    PoolViewModel,
    ProjectToPoolViewModelMap,
    ProjectToSizeViewModelMap,
    RegionViewModel,
    RegionViewModelMap,
    ScheduleViewModel,
    SizeViewModel,
} from '../models';
import { CustomizationSettingsComponent } from './add-dev-box-form-customization-settings-controls';
import AddDevBoxFormImageControl from './add-dev-box-form-image-control';
import AddDevBoxFormNameControl from './add-dev-box-form-name-control';
import AddDevBoxFormPoolControls from './add-dev-box-form-pool-controls';
import AddDevBoxFormProjectControls from './add-dev-box-form-project-controls';
import AddDevBoxFormRegionControl from './add-dev-box-form-region-control';
import AddDevBoxFormSizeControl from './add-dev-box-form-size-control';

interface AddDevBoxFormFieldGroupProps {
    isSubmitting: boolean;
    projects: AddDevBoxFormProjectViewModel[];
    pools: ProjectToPoolViewModelMap;
    images: ImageViewModelMap;
    regions: RegionViewModelMap;
    sizes: ProjectToSizeViewModelMap;
    devBoxName: string;
    selectedProject: AddDevBoxFormProjectViewModel | undefined;
    selectedPool: PoolViewModel | undefined;
    selectedImage: ImageViewModel | undefined;
    selectedRegion: RegionViewModel | undefined;
    selectedSize: SizeViewModel | undefined;
    fileCustomizations: CustomizationData | undefined;
    selectedIntrinsicTasks: IntrinsicTask[] | undefined;
    didSomeProjectFailToLoadCreateResources: boolean;
    isSelectedProjectLimitReached: (project: AddDevBoxFormProjectViewModel | undefined) => boolean;
    projectOnChange: (value: AddDevBoxFormProjectViewModel | undefined) => void;
    customizationsOnChange: (value: CustomizationData | undefined) => void;
    intrinsicTasksOnChange: (value: IntrinsicTask[] | undefined) => void;
    projectsAuthorizedForDevBoxCustomize: SerializableMap<ProjectResource | ProjectFromDiscoveryService>;
    statusForValidateCustomizationTasks: Status;
    validationResult: CustomizationTaskListValidationStatus | undefined;
    errors: CustomizationTaskListValidationError[] | undefined;
    isLoading: boolean;
    hasCustomizationTaskDefinitions: boolean;
    scheduleForSelectedSize: ScheduleViewModel | undefined;
    recentlyUsedImages: SerializableMap<Date>;
    recentlyUsedProjects: SerializableMap<Date>;
    isInMicrosoftTenant: boolean;
}

interface MultiPageCustomizationsFormFieldGroupProps {
    hasCustomizationTaskDefinitions: boolean;
    isLoading: boolean;
}

const messages = defineMessages({
    addDevBoxFormFieldGroupEnableCustomizationsCheckboxLabel: {
        id: 'AddDevBoxFormFieldGroup_EnableCustomizationsCheckbox_Label',
        defaultMessage: 'Apply customizations',
        description: 'Checkbox label for apply customizations',
    },
    addDevBoxFormFieldGroupEnableCustomizationsCheckboxAriaLabel: {
        id: 'AddDevBoxFormFieldGroup_EnableCustomizationsCheckbox_AriaLabel',
        defaultMessage: 'Apply customizations',
        description: 'Aria label for apply customizations',
    },
    customizationsFieldAriaLabel: {
        id: 'AddDevBoxFormFieldGroup_CustomizationsTag_AriaLabel',
        defaultMessage: 'Customizations',
        description: 'Aria label for customizations field.',
    },
});

const customizationsLearnMoreMessageValues = {
    Link: (chunks: string) => (
        <Link href={SupportSectionLinks.CustomizationsLink} target="_blank">
            {chunks}
        </Link>
    ),
};

const customizationsBoldValues = {
    b: (chunks: string) => <b>{chunks}</b>,
};

/**
 * Styles
 */

const useLabelStyles = makeStyles({
    root: {
        fontWeight: FontWeights.regular,
        paddingLeft: '4px',
    },
});

const useInfoIconStyles = makeStyles({
    root: {
        paddingTop: '3px',
    },
});

const useContainerStyles = makeStyles({
    root: {
        gap: '16px',
    },
});

const useInfoStyles = makeStyles({
    root: {
        gap: '8px',
    },
});

const useHibernateAndScheduleContainerStyles = makeStyles({
    root: {
        gap: '5px',
    },
});

/**
 * End Styles
 */

const shimmerElements: IShimmerElement[] = [{ type: ShimmerElementType.line, height: 24 }];

const MultiPageCustomizationsFormFieldGroup: React.FC<MultiPageCustomizationsFormFieldGroupProps> = (props) => {
    const { hasCustomizationTaskDefinitions, isLoading } = props;

    const { formatMessage } = useIntl();

    // Style hooks
    const labelStyles = useLabelStyles();
    const infoIconStyles = useInfoIconStyles();
    const stackStyles = useStackWithFullWidthItemStyles();
    const containerStyles = useContainerStyles();
    const horizontalStackStyles = useHorizontalStackStyles();
    const infoStyles = useInfoStyles();

    const onRenderLabel = React.useCallback(() => {
        return (
            <Label aria-label={formatMessage(messages.customizationsFieldAriaLabel)} styles={labelStyles}>
                <FormattedMessage
                    id="AddDevBoxFormFieldGroup_Label"
                    defaultMessage="Apply customizations"
                    description="Label for customizations info field"
                />
            </Label>
        );
    }, [formatMessage, labelStyles]);
    if (isLoading) {
        return (
            <div className={mergeClasses(stackStyles.root, containerStyles.root)}>
                <div className={stackStyles.item}>
                    <Separator />
                </div>
                <div className={stackStyles.item}>
                    <Shimmer shimmerElements={shimmerElements} />
                </div>
                <div className={stackStyles.item}>
                    <Shimmer shimmerElements={shimmerElements} />
                </div>
            </div>
        );
    }

    if (!hasCustomizationTaskDefinitions) {
        return (
            <div className={mergeClasses(stackStyles.root, containerStyles.root)}>
                <div className={stackStyles.item}>
                    <Separator />
                </div>
                <div className={stackStyles.item}>
                    <FormattedMessage
                        id="AddDevBoxFormFieldGroup_CustomizationsNotEnabledPartTwo_Text"
                        defaultMessage="<b>Customizations</b> is not enabled."
                        description="Text to let users know that customizations are not enabled for the project, <b></b> tags should not be localized."
                        values={customizationsBoldValues}
                    />
                </div>
                <div className={mergeClasses(horizontalStackStyles.root, infoStyles.root)}>
                    <div className={mergeClasses(horizontalStackStyles.item, infoIconStyles.root)}>
                        <Icon iconName={FluentIconNames.Info} />
                    </div>
                    <div className={horizontalStackStyles.item}>
                        <FormattedMessage
                            id="AddDevBoxFormFieldGroup_NoTaskDefinitions_Text"
                            defaultMessage="To customize this dev box please ask the project admin to <Link>add a customization catalog with tasks</Link> to the project."
                            description="Text to let users know that they need to contact their project admin in order to customize their dev box. <Link></Link> tags should not be localized."
                            values={customizationsLearnMoreMessageValues}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={mergeClasses(stackStyles.root, containerStyles.root)}>
            <div className={stackStyles.item}>
                <Separator />
            </div>
            <div className={stackStyles.item}>
                <FormattedMessage
                    id="AddDevBoxFormFieldGroup_Text"
                    defaultMessage="Configuration-as-code customizations will allow you to create a fully personalized ready-to-code environment. <Link>Learn more.</Link>"
                    description="Text giving information about customizations with a learn more link. <Link></Link> tags should not be localized."
                    values={customizationsLearnMoreMessageValues}
                />
            </div>

            <div className={stackStyles.item}>
                <Field<boolean> name={AddDevBoxFormField.ApplyCustomizationsEnabled}>
                    {(fieldProps) => {
                        const { input } = fieldProps;
                        const { onChange, value } = input;

                        // Callback hooks
                        const onCustomizationsCheckboxChange = React.useCallback(
                            (_event: unknown, checked?: boolean) => {
                                onChange(checked ?? false);
                            },
                            [onChange]
                        );

                        return (
                            <Checkbox
                                checked={value}
                                onChange={onCustomizationsCheckboxChange}
                                onRenderLabel={onRenderLabel}
                                ariaLabel={formatMessage(
                                    messages.addDevBoxFormFieldGroupEnableCustomizationsCheckboxAriaLabel
                                )}
                            />
                        );
                    }}
                </Field>
            </div>
        </div>
    );
};

export const AddDevBoxFormFieldGroup: React.FC<AddDevBoxFormFieldGroupProps> = (props) => {
    const {
        isSubmitting,
        projects,
        pools,
        images,
        regions,
        sizes,
        selectedProject,
        devBoxName,
        selectedPool,
        selectedImage,
        selectedRegion,
        selectedSize,
        selectedIntrinsicTasks,
        didSomeProjectFailToLoadCreateResources,
        isSelectedProjectLimitReached,
        projectOnChange,
        intrinsicTasksOnChange,
        projectsAuthorizedForDevBoxCustomize,
        isLoading,
        hasCustomizationTaskDefinitions,
        scheduleForSelectedSize,
        recentlyUsedImages,
        recentlyUsedProjects,
        isInMicrosoftTenant,
    } = props;

    const isRoundTripTimeEnabled = React.useMemo(
        () =>
            (isFeatureFlagEnabled(FeatureFlagName.RoundTripTime) || isInMicrosoftTenant) && values(regions).length > 0,
        [isInMicrosoftTenant, regions]
    );

    // Callback hooks
    const userCanCustomizeSelectedProject = React.useMemo(() => {
        if (selectedProject) {
            return has(projectsAuthorizedForDevBoxCustomize, selectedProject.id);
        }

        return false;
    }, [selectedProject, projectsAuthorizedForDevBoxCustomize]);
    const isCustomizationPreviewFeaturesEnabled = isFeatureFlagEnabled(
        FeatureFlagName.EnableIntrinsicTasksForCustomization
    );

    // Style hooks
    const stackStyles = useStackWithFullWidthItemStyles();
    const containerStyles = useContainerStyles();
    const hibernateAndScheduleContainerStyles = useHibernateAndScheduleContainerStyles();

    return (
        <div className={mergeClasses(stackStyles.root, containerStyles.root)}>
            <div className={stackStyles.item}>
                <Field<string> name={AddDevBoxFormField.DevBoxName}>
                    {(fieldProps) => {
                        const { input, meta } = fieldProps;
                        const { onChange } = input;
                        const { modified, active, error } = meta;

                        return (
                            <AddDevBoxFormNameControl
                                devBoxName={devBoxName}
                                onChange={onChange}
                                disabled={isSubmitting}
                                errorMessage={modified && !active ? error : undefined}
                            />
                        );
                    }}
                </Field>
            </div>
            <div className={stackStyles.item}>
                <Field<AddDevBoxFormProjectViewModel> name={AddDevBoxFormField.SelectedProject}>
                    {(fieldProps) => {
                        const { meta, input } = fieldProps;
                        const { modified, error, active } = meta;

                        // IMPORTANT: Force field validation when component mounts if project has limit and is readonly.
                        React.useEffect(() => {
                            if (
                                projects.length <= 1 &&
                                selectedProject &&
                                isSelectedProjectLimitReached(selectedProject)
                            ) {
                                // This triggers validation even for readonly fields
                                input.onBlur();
                            }
                        }, [projects, selectedProject, isSelectedProjectLimitReached, input]);
                        const showError = React.useMemo(
                            () =>
                                (modified && !active) ||
                                (projects.length <= 1 && isSelectedProjectLimitReached(selectedProject)),
                            [modified, active, projects, selectedProject]
                        );

                        return (
                            <AddDevBoxFormProjectControls
                                projects={projects}
                                disabled={isSubmitting}
                                onChange={projectOnChange}
                                selectedProject={selectedProject}
                                recentlyUsedProjects={recentlyUsedProjects}
                                errorMessage={showError ? error : undefined}
                                showFailedToLoadWarning={didSomeProjectFailToLoadCreateResources}
                            />
                        );
                    }}
                </Field>
            </div>
            {!isRoundTripTimeEnabled && (
                <div className={stackStyles.item}>
                    <Field<PoolViewModel> name={AddDevBoxFormField.SelectedPool}>
                        {(fieldProps) => {
                            const { input, meta } = fieldProps;
                            const { onChange } = input;
                            const { modified, active, error } = meta;

                            // Note: this is placed here because selectedProject will be undefined until it is registered (i.e. when we use 'Field').
                            // Only when its registered, will the initial value be set for the field.
                            const selectedProjectId = React.useMemo(() => selectedProject?.id, [selectedProject]);

                            if (isSelectedProjectLimitReached(selectedProject)) {
                                return <></>;
                            }

                            return (
                                <AddDevBoxFormPoolControls
                                    poolsByProject={pools}
                                    disabled={isSubmitting}
                                    onChange={onChange}
                                    selectedProjectId={selectedProjectId}
                                    selectedPool={selectedPool}
                                    errorMessage={modified && !active ? error : undefined}
                                />
                            );
                        }}
                    </Field>
                </div>
            )}
            {!isRoundTripTimeEnabled && selectedPool?.architecture && (
                <div className={stackStyles.item}>
                    <ArchitectureReadonlyField architecture={selectedPool.architecture} />
                </div>
            )}
            {selectedPool && (
                <div className={stackStyles.item}>
                    <HibernateReadonlyField
                        hibernateSupport={selectedPool.hibernateSupport}
                        isInMicrosoftTenant={isInMicrosoftTenant}
                    />
                </div>
            )}
            {isRoundTripTimeEnabled && (
                <div className={stackStyles.item}>
                    <Field<ImageViewModel> name={AddDevBoxFormField.SelectedImage}>
                        {(fieldProps) => {
                            const { input, meta } = fieldProps;
                            const { onChange } = input;
                            const { modified, active, error } = meta;

                            // Note: this is placed here because selectedProject will be undefined until it is registered (i.e. when we use 'Field').
                            // Only when its registered, will the initial value be set for the field.
                            const selectedProjectId = React.useMemo(() => selectedProject?.id, [selectedProject]);

                            if (isSelectedProjectLimitReached(selectedProject)) {
                                return <></>;
                            }

                            return (
                                <AddDevBoxFormImageControl
                                    imagesToPools={images}
                                    disabled={isSubmitting}
                                    onChange={onChange}
                                    selectedProjectId={selectedProjectId}
                                    selectedImage={selectedImage}
                                    recentlyUsedImages={recentlyUsedImages}
                                    errorMessage={modified && !active ? error : undefined}
                                />
                            );
                        }}
                    </Field>
                </div>
            )}
            {selectedImage && (
                <div className={stackStyles.item}>
                    <Field<RegionViewModel> name={AddDevBoxFormField.SelectedRegion}>
                        {(fieldProps) => {
                            const { input, meta } = fieldProps;
                            const { onChange } = input;
                            const { modified, active, error } = meta;

                            // Note: this is placed here because selectedProject will be undefined until it is registered (i.e. when we use 'Field').
                            // Only when its registered, will the initial value be set for the field.
                            const selectedProjectId = React.useMemo(() => selectedProject?.id, [selectedProject]);

                            const selectedImageId = React.useMemo(() => selectedImage?.imageName, [selectedImage]);

                            return (
                                <AddDevBoxFormRegionControl
                                    imagesToPools={images}
                                    regionRecommendations={regions}
                                    disabled={isSubmitting}
                                    onChange={onChange}
                                    selectedImageId={selectedImageId}
                                    selectedProjectId={selectedProjectId}
                                    selectedRegion={selectedRegion}
                                    errorMessage={modified && !active ? error : undefined}
                                />
                            );
                        }}
                    </Field>
                </div>
            )}
            {selectedRegion && (
                <div className={stackStyles.item}>
                    <Field<SizeViewModel> name={AddDevBoxFormField.SelectedSize}>
                        {(fieldProps) => {
                            const { input, meta } = fieldProps;
                            const { onChange } = input;
                            const { modified, active, error } = meta;

                            // Note: this is placed here because selectedProject will be undefined until it is registered (i.e. when we use 'Field').
                            // Only when its registered, will the initial value will be set for the field.
                            const selectedProjectId = React.useMemo(() => selectedProject?.id, [selectedProject]);

                            const selectedImageId = React.useMemo(() => selectedImage?.imageName, [selectedImage]);

                            const selectedRegionId = React.useMemo(() => selectedRegion.name, [selectedRegion]);

                            return (
                                <AddDevBoxFormSizeControl
                                    projectToSizesMap={sizes}
                                    disabled={isSubmitting}
                                    onChange={onChange}
                                    selectedProjectId={selectedProjectId}
                                    selectedImageId={selectedImageId}
                                    selectedRegionId={selectedRegionId}
                                    selectedSize={selectedSize}
                                    errorMessage={modified && !active ? error : undefined}
                                />
                            );
                        }}
                    </Field>
                </div>
            )}
            {selectedSize?.architecture && (
                <div className={stackStyles.item}>
                    <ArchitectureReadonlyField architecture={selectedSize.architecture} />
                </div>
            )}
            {selectedSize && selectedRegion && selectedImage && (
                <div className={mergeClasses(stackStyles.root, hibernateAndScheduleContainerStyles.root)}>
                    <div className={stackStyles.item}>
                        <HibernateReadonlyField
                            hibernateSupport={selectedSize.hibernateSupport}
                            isInMicrosoftTenant={isInMicrosoftTenant}
                        />
                    </div>
                    {scheduleForSelectedSize && <ScheduleReadonlyField schedule={scheduleForSelectedSize} />}
                </div>
            )}
            {selectedProject && userCanCustomizeSelectedProject && (
                <div className={stackStyles.item}>
                    <MultiPageCustomizationsFormFieldGroup
                        hasCustomizationTaskDefinitions={hasCustomizationTaskDefinitions}
                        isLoading={isLoading}
                    />
                    {isCustomizationPreviewFeaturesEnabled && (
                        // TODO: Work item 2244557. Will come up with a proper name for this component.
                        <CustomizationSettingsComponent
                            selectedIntrinsicTasks={selectedIntrinsicTasks}
                            intrinsicTasksOnChange={intrinsicTasksOnChange}
                            devBoxName={devBoxName}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default AddDevBoxFormFieldGroup;
